'use client';

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarOutlined, InfoCircleOutlined, LineChartOutlined } from '@ant-design/icons';
import { Button, Empty, Tabs, Typography } from 'antd';

import { CardListBanner, CardListRepost } from './components';

import { formatDates } from 'features/helpers/dates';
import { encodeLinkOrder } from 'features/helpers/encodeLinkStats';
import { StatisticCardRepost } from 'components/common/StatisticCardRepost';

import type { CampaignsListItemProps } from './types';

const { Text } = Typography;
const { TabPane } = Tabs;

export const CampaignsListItem = ({ campaign }: CampaignsListItemProps) => {
  const [activeTab, setActiveTab] = useState<string>('banner');

  // Check if we should show tabs
  const hasBannerStats =
    campaign.combineStatisticBanner &&
    campaign.combineStatisticBanner.orders &&
    campaign.combineStatisticBanner.orders.length > 0;
  const hasRepostStats =
    campaign.combineStatisticRepost &&
    campaign.combineStatisticRepost.orders &&
    campaign.combineStatisticRepost.orders.length > 0;
  const shouldShowTabs = hasBannerStats && hasRepostStats;

  // Determine which statistics to show based on active tab and available data
  const showBannerStats = activeTab === 'banner' && hasBannerStats;
  const showRepostStats = activeTab === 'repost' && hasRepostStats;

  // If tabs are not shown, show whatever data is available
  const showOnlyBannerStats = !shouldShowTabs && hasBannerStats;
  const showOnlyRepostStats = !shouldShowTabs && hasRepostStats;

  // Generate links for statistics
  const getBannerStatsLink = useCallback(() => {
    if (hasBannerStats && campaign.combineStatisticBanner.orders.length > 0) {
      const order = campaign.combineStatisticBanner.orders[0];
      return encodeLinkOrder(order.id, campaign.combineStatisticBanner.orderVariant);
    }
    return null;
  }, [campaign.combineStatisticBanner, hasBannerStats]);

  const getRepostStatsLink = useCallback(() => {
    if (hasRepostStats && campaign.combineStatisticRepost.orders.length > 0) {
      const order = campaign.combineStatisticRepost.orders[0];
      return encodeLinkOrder(order.id, campaign.combineStatisticRepost.orderVariant);
    }
    return null;
  }, [campaign.combineStatisticRepost, hasRepostStats]);

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // Render banner statistics
  const renderBannerStats = () => {
    const bannerStatsLink = getBannerStatsLink();

    return (
      <div className="w-full">
        <CardListBanner statistics={campaign.combineStatisticBanner} rangeDate={{ endDate: null, startDate: null }} />

        {bannerStatsLink && (
          <div className="mt-2 text-right">
            <Link to={bannerStatsLink}>
              <Button type="primary" size="small" icon={<LineChartOutlined />}>
                Статистика баннеров
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  };

  // Render repost statistics
  const renderRepostStats = () => {
    const repostStatsLink = getRepostStatsLink();

    return (
      <div className="w-full">
        <CardListRepost statistics={campaign.combineStatisticRepost} />

        {repostStatsLink && (
          <div className="mt-2 text-right">
            <Link to={repostStatsLink}>
              <Button type="primary" size="small" icon={<LineChartOutlined />}>
                Статистика пост
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  };

  // Get dates for display
  const getDates = () => {
    // Try to get dates from banner data
    if (hasBannerStats) {
      const allDates = campaign.combineStatisticBanner.orders.flatMap((order) => order.dates || []);
      if (allDates.length > 0) {
        // Filter for unique dates
        const uniqueDates = [...new Set(allDates)];
        return formatDates(uniqueDates);
      }
    }

    // Try to get dates from repost data
    if (hasRepostStats) {
      const allDates = campaign.combineStatisticRepost.orders.flatMap((order) =>
        order.dateRepost ? [order.dateRepost] : [],
      );
      if (allDates.length > 0) {
        // Filter for unique dates
        const uniqueDates = [...new Set(allDates)];
        return formatDates(uniqueDates);
      }
    }

    return null;
  };

  return (
    <div
      className={`mb-4 cursor-pointer rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300`}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-5 sm:grid-rows-2">
        <div className="flex-none col-span-1 row-span-1">
          <h4 className="text-lg font-medium mb-1 whitespace-nowrap">{campaign.name}</h4>
          {getDates() ? (
            <div className="flex gap-2 items-center text-gray-500">
              <CalendarOutlined className="mr-1" />
              <span>{getDates()}</span>
            </div>
          ) : (
            <div className="flex items-center text-gray-500">
              <InfoCircleOutlined className="mr-1" />
              <span>Нет данных о датах</span>
            </div>
          )}
        </div>

        <div className="flex-1 col-span-4 row-span-2">
          {shouldShowTabs && (
            <Tabs activeKey={activeTab} onChange={handleTabChange} size="small" style={{ marginBottom: 4 }}>
              <TabPane tab="Баннер" key="banner" />
              <TabPane tab="Пост" key="repost" />
            </Tabs>
          )}

          <div>
            {(showBannerStats || showOnlyBannerStats) && renderBannerStats()}
            {(showRepostStats || showOnlyRepostStats) && renderRepostStats()}

            {!hasBannerStats && !hasRepostStats && (
              <div className="w-full text-center">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      Статистика отсутствует
                    </Text>
                  }
                  style={{ margin: 0 }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
